import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { authenticateWithDiscord, isEmbedded } from '../discord/sdk';
import { PREVIEW_GUILD_ID, configureApi, request, setActiveProfile } from '../lib/spoylApi';
import {
  fetchStudioSession,
  readSignedOutReason,
  signIn,
  signOut,
  type SignedOutReason,
} from '../web/studioSession';
import { DEMO_ECONOMY, DEMO_USER } from '../mock/data';
import { DEFAULT_PROFILE_THEME, OWNER_PROFILE_ID } from '@shared';
import type { ProfileTheme, ProfileView, PublicMascot, PublicProfile, StudioGuild } from '@shared';
import type { DiscordContext, Economy, SpoylUser, UserRole } from '../types';

/**
 * `signed-out` and `denied` belong to the studio alone: inside Discord there is
 * no such thing as being signed out, and no such thing as reaching the app
 * without the client having already said who you are.
 */
export type SessionStatus = 'connecting' | 'ready' | 'error' | 'signed-out' | 'denied';

/**
 * `discord` — running inside the client.
 * `preview` — plain browser tab against a dev backend, role switchable.
 * `web` — the studio on its own domain, behind a real Discord sign-in.
 */
export type SessionMode = 'discord' | 'preview' | 'web';

/**
 * Which of the three we are in.
 *
 * A production build in a plain tab is always the studio: the browser preview
 * is a development affordance and shipping it as the fallback would mean a
 * built app served anywhere at all offering a role switcher. The query
 * overrides exist so either can be driven locally.
 */
function resolveMode(): SessionMode {
  if (isEmbedded()) return 'discord';
  const params = new URLSearchParams(window.location.search);
  if (params.get('web') === '1') return 'web';
  if (params.get('preview') === '1') return 'preview';
  return import.meta.env.DEV ? 'preview' : 'web';
}

interface SessionValue {
  status: SessionStatus;
  mode: SessionMode;
  /**
   * Resolved by the backend, never trusted from the client: the guild owner
   * gets every console, a manager the one profile the registry names, everyone
   * else the game.
   */
  role: UserRole;
  user: SpoylUser | null;
  context: DiscordContext;
  /** Every model profile in the guild — what the picker renders. */
  profiles: PublicProfile[];
  /**
   * The active creator's theme. Derived from `profiles` + `activeProfileId`,
   * exposed because the console needs the *name* of it — to offer her palette —
   * where everything visual already reads the CSS tokens it stamps.
   */
  theme: ProfileTheme;
  /** The active realm's mascot art, or null — every reader then draws the placeholder. */
  mascot: PublicMascot | null;
  /**
   * The Discord server this is running in, as its members see it. Null in the
   * browser preview and when the `guilds` scope was refused, and every reader
   * falls back to Spoyl's own mark rather than assuming it exists.
   */
  guild: { name: string; iconUrl: string | null } | null;
  /**
   * Studio only: every realm she may run, so the top bar can offer the switch.
   * Empty inside Discord and in the preview, where the guild is a given.
   */
  studioGuilds: StudioGuild[];
  /** The guild every request is addressed to. Null until the boot resolves it. */
  activeGuildId: string | null;
  /** Studio only: opens another of her realms. Reloads the page. */
  selectGuild: (guildId: string) => void;
  /**
   * The profile every request is scoped to. Null means none picked yet, which
   * for a client renders the picker. Owners and managers always have one.
   */
  activeProfileId: string | null;
  /** Picks a profile (null sends a client back to the picker). */
  selectProfile: (profileId: string | null) => void;
  /** Re-reads the profile list — after the owner creates or renames one. */
  refreshProfiles: () => Promise<void>;
  /**
   * Level and XP. Still placeholder data — unlike credits, progression has no
   * server-side implementation yet.
   */
  economy: Economy;
  error: string | null;
  /** Preview-only affordance to switch role; a no-op inside Discord. */
  setPreviewRole: (role: UserRole) => void;
  /** Why the studio is showing a sign-in screen rather than a console. */
  signedOutReason: SignedOutReason;
  /** Studio only: hands the page to Discord, and to the sign-out route. */
  signIn: () => void;
  signOut: () => Promise<void>;
}

const EMPTY_CONTEXT: DiscordContext = { guildId: null, channelId: null, instanceId: null };

/** What the preview falls back to when no dev server answers `/profile`. */
const FALLBACK_PROFILES: PublicProfile[] = [
  {
    id: OWNER_PROFILE_ID,
    name: 'Creator',
    avatar: null,
    theme: DEFAULT_PROFILE_THEME,
    mascot: null,
  },
];

const SessionContext = createContext<SessionValue | null>(null);

/**
 * Read once, at module load, for the same reason the Discord handshake is
 * memoised: `readSignedOutReason` scrubs the query string as it reads it, so a
 * StrictMode remount would find nothing the second time and quietly replace a
 * real refusal with "no reason given".
 */
const INITIAL_SIGNED_OUT_REASON: SignedOutReason = readSignedOutReason();

/**
 * Where the studio remembers which realm it was last opened on.
 *
 * Remembered rather than asked every time: a creator lives in one of these and
 * would answer the same question every morning.
 */
const GUILD_CHOICE_KEY = 'spoyl.studio.guild';

/** The remembered realm, or null when storage is unavailable or empty. */
function rememberedGuild(): string | null {
  try {
    return window.localStorage.getItem(GUILD_CHOICE_KEY);
  } catch {
    // Private browsing and blocked storage both throw here. Losing the memory
    // of a choice is survivable; failing to boot over it is not.
    return null;
  }
}

/**
 * Which of her realms the studio opens on.
 *
 * `?guild=` wins over the remembered value so the switch below works even where
 * storage is blocked, and so a bookmark can name a realm. Both are checked
 * against the list the server just sent: losing access to a guild falls back to
 * one she still holds instead of addressing one that would answer 403.
 */
function pickStudioGuild(guilds: StudioGuild[]): string | null {
  const requested = new URLSearchParams(window.location.search).get('guild');
  const wanted = requested ?? rememberedGuild();
  const match = guilds.find((guild) => guild.guildId === wanted);
  return (match ?? guilds[0])?.guildId ?? null;
}

/**
 * Switches realm, by reloading the page.
 *
 * A guild change moves the role, the profile list, the theme and every balance
 * at once. Swapping it in place would mean keying both stores on the guild as
 * well as the profile, and any reader that missed the change would answer for
 * the previous realm — the exact leak the profile keying already exists to
 * prevent. This is a rare action on an ordinary web page, so it takes the
 * reload and keeps the guarantee.
 */
function selectStudioGuild(guildId: string): void {
  try {
    window.localStorage.setItem(GUILD_CHOICE_KEY, guildId);
  } catch {
    // The query parameter below carries the choice on its own.
  }
  const url = new URL(window.location.href);
  url.searchParams.set('guild', guildId);
  window.location.replace(url.toString());
}

/** Reads a preview role override from `?role=`, defaulting to owner. */
function readPreviewRole(): UserRole {
  const params = new URLSearchParams(window.location.search);
  const role = params.get('role');
  return role === 'client' || role === 'manager' ? role : 'owner';
}

/**
 * The profile a session starts scoped to. Owners open their own game's
 * console, managers the one profile they run, and a client goes straight in
 * when there is only one creator — the picker earns its screen only when
 * there is an actual choice to make.
 */
function initialProfileId(
  role: UserRole,
  profiles: PublicProfile[],
  managedProfileId: string | null,
): string | null {
  if (role === 'owner') return OWNER_PROFILE_ID;
  if (role === 'manager') return managedProfileId ?? OWNER_PROFILE_ID;
  return profiles.length === 1 ? (profiles[0]?.id ?? null) : null;
}

/**
 * Owns the boot sequence of the activity: the Discord handshake when embedded,
 * a preview identity in a browser tab. Everything downstream can assume a user
 * exists, the API is configured, the role is settled and — except for a client
 * still on the picker — a profile is active once `status` is `ready`.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('connecting');
  const [user, setUser] = useState<SpoylUser | null>(null);
  const [context, setContext] = useState<DiscordContext>(EMPTY_CONTEXT);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState<UserRole>(readPreviewRole);
  const [profiles, setProfiles] = useState<PublicProfile[]>([]);
  const [guild, setGuild] = useState<SessionValue['guild']>(null);
  const [studioGuilds, setStudioGuilds] = useState<StudioGuild[]>([]);
  const [activeGuildId, setActiveGuildId] = useState<string | null>(null);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);

  const mode: SessionMode = resolveMode();

  /**
   * The active creator's colour, stamped on the document root.
   *
   * This one line is the whole feature: `tokens.css` overrides the brand ramp
   * per theme, so console, studio and game all change together and nothing
   * downstream ever branches on which creator is active. Back to the house
   * colour whenever no profile is — the picker and the sign-in screen belong to
   * Spoyl, not to any one model.
   */
  const theme =
    profiles.find((profile) => profile.id === activeProfileId)?.theme ?? DEFAULT_PROFILE_THEME;

  const mascot = profiles.find((profile) => profile.id === activeProfileId)?.mascot ?? null;

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  /** Applies a profile choice to both the API module and the React tree. */
  function applyProfile(profileId: string | null): void {
    setActiveProfile(profileId);
    setActiveProfileId(profileId);
  }

  useEffect(() => {
    let cancelled = false;

    /** Applies what `/profile` answered: role, the list, the starting scope. */
    function applyProfileView(view: ProfileView): void {
      setRole(view.role);
      setProfiles(view.profiles);
      setGuild(view.guild);
      applyProfile(initialProfileId(view.role, view.profiles, view.managedProfileId));
    }

    async function boot() {
      /**
       * The studio: a sealed cookie stands in for the whole Discord handshake.
       *
       * The server is asked first — it is the only party that knows which
       * guilds this instance serves, and the only one entitled to say whether
       * she is staff in any of them. Once it answers, every request after this
       * one is the same API the iframe calls, addressed the same way.
       */
      if (mode === 'web') {
        try {
          const result = await fetchStudioSession();
          if (cancelled) return;

          if (result.kind !== 'session') {
            setStatus(result.kind === 'denied' ? 'denied' : 'signed-out');
            return;
          }

          // One realm is the ordinary case and gets no extra screen; the
          // switch only appears when there is a second one to switch to.
          const chosenGuildId = pickStudioGuild(result.session.guilds);
          if (!chosenGuildId) {
            // The server refuses to open a session with no guilds in it, so
            // this is unreachable rather than merely unlikely — treated as a
            // refusal because a console addressed to nothing is worse.
            setStatus('denied');
            return;
          }
          setStudioGuilds(result.session.guilds);
          setActiveGuildId(chosenGuildId);

          configureApi({
            guildId: chosenGuildId,
            accessToken: null,
            devUser: null,
            devRole: 'client',
            cookie: true,
          });

          // Identity, role and the profile list all come from the same route
          // the activity uses. The studio learns nothing extra about itself.
          const profile = await request<ProfileView>('/profile');
          if (cancelled) return;
          setUser(profile.user);
          applyProfileView(profile);
          setStatus('ready');
        } catch (cause) {
          if (cancelled) return;
          // eslint-disable-next-line no-console
          console.error('[spoyl] studio session failed:', cause);
          setError(cause instanceof Error ? cause.message : 'Could not reach the studio backend');
          setStatus('error');
        }
        return;
      }

      if (mode === 'preview') {
        const previewRole = readPreviewRole();
        configureApi({
          guildId: PREVIEW_GUILD_ID,
          accessToken: null,
          devUser: DEMO_USER,
          devRole: previewRole,
        });
        setActiveGuildId(PREVIEW_GUILD_ID);
        setUser(DEMO_USER);
        // The dev backend resolves the preview identity like any other — but a
        // preview with no server running must still render, so a failure falls
        // back to a single default profile instead of an error screen.
        try {
          const profile = await request<ProfileView>('/profile');
          if (cancelled) return;
          applyProfileView(profile);
        } catch {
          if (cancelled) return;
          setProfiles(FALLBACK_PROFILES);
          applyProfile(initialProfileId(previewRole, FALLBACK_PROFILES, null));
        }
        setStatus('ready');
        return;
      }

      try {
        const session = await authenticateWithDiscord();
        if (cancelled) return;

        // An economy belongs to a guild. Launched from a DM or a group call
        // there is nothing to belong to, and saying so beats a game whose every
        // request fails.
        if (!session.context.guildId) {
          throw new Error('Spoyl has to be launched from a server, not a private call.');
        }

        configureApi({
          guildId: session.context.guildId,
          accessToken: session.accessToken,
          devUser: null,
          devRole: 'client',
        });
        setActiveGuildId(session.context.guildId);
        setUser(session.user);
        setContext(session.context);

        // The backend is the single source of truth for who may see the
        // console. A failure here leaves the role at its default rather than
        // handing out console access on a hiccup.
        const profile = await request<ProfileView>('/profile');
        if (cancelled) return;
        applyProfileView(profile);
        setStatus('ready');
      } catch (cause) {
        if (cancelled) return;
        // Discord SDK rejections are often plain RPC objects, not Error
        // instances — extract their code/message instead of losing them.
        // eslint-disable-next-line no-console
        console.error('[spoyl] Discord connection failed:', cause);
        const message =
          cause instanceof Error
            ? cause.message
            : typeof cause === 'object' && cause !== null
              ? JSON.stringify(cause)
              : String(cause);
        setError(message || 'Unknown connection error');
        setStatus('error');
      }
    }

    void boot();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const value = useMemo<SessionValue>(
    () => ({
      status,
      mode,
      role,
      user,
      context,
      profiles,
      theme,
      mascot,
      guild,
      studioGuilds,
      activeGuildId,
      selectGuild: selectStudioGuild,
      activeProfileId,
      selectProfile: applyProfile,
      refreshProfiles: async () => {
        try {
          const profile = await request<ProfileView>('/profile');
          setProfiles(profile.profiles);
          setGuild(profile.guild);
        } catch {
          // The list the session already holds stays good enough.
        }
      },
      economy: DEMO_ECONOMY,
      error,
      setPreviewRole: (next: UserRole) => {
        if (mode !== 'preview') return;
        configureApi({
          guildId: PREVIEW_GUILD_ID,
          accessToken: null,
          devUser: DEMO_USER,
          devRole: next,
        });
        setRole(next);
        const url = new URL(window.location.href);
        url.searchParams.set('role', next);
        window.history.replaceState(null, '', url);
        // The scope follows the role: the server resolves what a manager may
        // run, so the switch re-asks it rather than guessing.
        void request<ProfileView>('/profile')
          .then((profile) => {
            setProfiles(profile.profiles);
            applyProfile(initialProfileId(next, profile.profiles, profile.managedProfileId));
          })
          .catch(() => {
            applyProfile(initialProfileId(next, profiles, null));
          });
      },
      signedOutReason: INITIAL_SIGNED_OUT_REASON,
      signIn,
      signOut,
    }),
    [
      status,
      mode,
      role,
      user,
      context,
      profiles,
      theme,
      mascot,
      guild,
      studioGuilds,
      activeGuildId,
      activeProfileId,
      error,
    ],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error('useSession must be used inside a SessionProvider');
  }
  return value;
}
